import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable, Subject, takeUntil } from 'rxjs';

import { InputComponent } from '../../../shared/input/input.component';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell.component';
import { CheckboxComponent } from '../../../shared/checkbox/checkbox.component';
import { SearchableObjectMultiSelectComponent } from '../../../shared/searchable-object-multi-select/searchable-object-multi-select.component';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';
import { resolveErrorMessage } from '../../../shared/utils/api-error.util';

import { AppService } from '../../../services/application/app.service';
import {
  ApplicationData,
  AppVersion,
  AppVersionDetails,
  Org,
  UpdateApplicationPayload,
  UpdateVersionPayload,
} from '../../../interfaces/application';
import { OrgService } from '../../../services/organization/org.service';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';

import {
  APP_DESCRIPTION_MAX_LENGTH,
  APP_VERSION_CONTAINER_NAME_MAX_LENGTH,
  APP_VERSION_REGEX,
} from '../shared-form-elements';
import { MatFormFieldModule } from '@angular/material/form-field';

export type EditApplicationModalProps = {
  title: string;
  application: ApplicationData;
  appVersion: AppVersionDetails;
  allAppVersions: AppVersion[];
  allOrgs: Org[];
};

type EditForm = {
  applicationName: FormControl<string | null>;
  description: FormControl<string | null>;
  version: FormControl<string | null>;
  containerName: FormControl<string | null>;
  inputs: FormControl<string | null>;
  selectedOrgs: FormControl<Org[] | null>;
  dependsOnAppVersions: FormControl<AppVersion[] | null>;
  multiRun: FormControl<boolean | null>;
  isPlugin: FormControl<boolean | null>;
};

@Component({
  selector: 'app-edit-application-modal',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    ModalShellComponent,
    SearchableObjectMultiSelectComponent,
    CheckboxComponent,
  ],
  templateUrl: './edit-application-modal.component.html',
  styleUrl: './edit-application-modal.component.css',
})
export class EditApplicationModalComponent implements OnInit, OnDestroy {
  editForm!: FormGroup<EditForm>;
  title!: EditApplicationModalProps['title'];
  application!: EditApplicationModalProps['application'];
  appVersion!: EditApplicationModalProps['appVersion'];
  allAppVersions!: EditApplicationModalProps['allAppVersions'];
  allOrgs!: EditApplicationModalProps['allOrgs'];

  isErrorVisible: boolean = false;
  errorMessage: string = '';

  private destroy$ = new Subject<void>();

  appNameLabel = $localize`Application name`;
  appNamePlaceholder = $localize`Enter Application name`;

  descriptionLabel = $localize`Description`;
  descriptionPlaceholder = $localize`Enter Description`;
  descriptionMaxLength = APP_DESCRIPTION_MAX_LENGTH;

  appVersionLabel = $localize`Application Version`;
  appVersionPlaceholder = $localize`X.Y.Z or W.X.Y.Z`;

  containerNameLabel = $localize`Container Name`;
  containerNamePlaceholder = $localize`Enter Container Name`;
  containerNameMaxLength = APP_VERSION_CONTAINER_NAME_MAX_LENGTH;

  inputTypeLabel = $localize`Input Type`;
  inputTypeSubLabel = $localize`(e.g., config, SV_panel, RepExp_panel)`;

  selectOrgsLabel = $localize`Organization(s)`;
  selectOrgsPlaceholder = $localize`Select one or more orgs`;
  selectOrgsOptions: Org[] = [];
  selectOrgsDisplayFn = (org: Org): string => org.name;
  selectOrgsCompareFn = (a: Org, b: Org): boolean => a.orgId === b.orgId;

  // versioned Applications that this version depends on
  dependsOnAppsLabel = $localize`Depends On Prerequisite Applications`;
  dependsOnAppsPlaceholder = $localize`None`;
  dependsOnAppsSelectOptions: AppVersion[] = [];
  dependsOnAppsSelectDisplayFn = (app: AppVersion): string => `${app.name} ${app.version}`;
  dependsOnAppsSelectCompareFn = (a: AppVersion, b: AppVersion): boolean => a.appVersionId === b.appVersionId;

  // whether this version can run against the combined output of multiple instrument runs
  multiRunLabel = $localize`Version Has Multi-Run Support`;

  // whether this application is a plugin
  isPluginLabel = $localize`Application Is A Plugin`;

  constructor(
    public dialogRef: MatDialogRef<EditApplicationModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: EditApplicationModalProps,
    private fb: FormBuilder,
    private appService: AppService,
    private organizationService: OrgService,
    private messageModal: MessageModalService
  ) {
    this.title = data.title;
    this.application = data.application;
    this.appVersion = data.appVersion;
    this.allAppVersions = data.allAppVersions;
    this.dependsOnAppsSelectOptions = data.allAppVersions;
    this.allOrgs = data.allOrgs;
    this.selectOrgsOptions = data.allOrgs;
  }

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    this.editForm = this.fb.group({
      applicationName: ['', [Validators.required, noWhitespaceValidator()]],
      description: ['', [Validators.maxLength(APP_DESCRIPTION_MAX_LENGTH), noWhitespaceValidator()]],
      version: ['', [Validators.required, Validators.pattern(APP_VERSION_REGEX), noWhitespaceValidator()]],
      containerName: [
        '',
        [Validators.required, Validators.maxLength(APP_VERSION_CONTAINER_NAME_MAX_LENGTH), noWhitespaceValidator()],
      ],
      inputs: ['', noWhitespaceValidator()],
      selectedOrgs: [[] as Org[]],
      dependsOnAppVersions: [[] as AppVersion[]],
      multiRun: [null as boolean | null],
      isPlugin: [null as boolean | null],
    });

    const formData = {
      applicationName: this.application.name,
      description: this.application.description,
      version: this.appVersion.version,
      containerName: this.appVersion.containerName,
      inputs: this.appVersion.inputs,
      selectedOrgs: this.appVersion.organizations,
      // 'prerequisiteApps' is a different data type than 'AppVersion', sharing only 'appVersionId'
      // find the full AppVersion in the options list to pre-load selections
      dependsOnAppVersions: this.appVersion.prerequisiteApps
        .map((prereq) => this.allAppVersions.find((appV) => appV.appVersionId === prereq.appVersionId))
        .filter((appV): appV is AppVersion => appV != null),
      multiRun: this.appVersion.multiRun,
      isPlugin: this.application.isPlugin,
    };

    this.editForm.patchValue(formData);
  }

  closePopup(): void {
    this.dialogRef.close();
  }

  handleSubmit(): void {
    this.isErrorVisible = false;

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const apiCalls = this.prepareEditApiCalls();

    // If no changes made
    if (apiCalls.length === 0) {
      this.dialogRef.close();
      return;
    }

    // making parallel apiCalls
    forkJoin(apiCalls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dialogRef.close({ success: true, action: 'edit' });
        },
        error: (err) => {
          console.error('One or more updates failed. Please try again.', err);
          this.handleApiError(err, `One or more updates failed. Please try again. ${err.message}`);
        },
      });
  }

  private prepareEditApiCalls(): Observable<any>[] {
    const apiCalls: Observable<any>[] = [];

    // for changes in Application details
    const appPayload = this.getChangedApplicationPayload();
    if (Object.keys(appPayload).length > 0) {
      apiCalls.push(this.appService.updateApplication(Number(this.application.appId), appPayload));
    }

    // for changes in Version details (Version, ContainerName, Inputs)
    const versionPayload = this.getChangedVersionPayload();
    if (Object.keys(versionPayload).length > 0) {
      apiCalls.push(this.appService.updateVersion(Number(this.appVersion.appVersionId), versionPayload));
    }

    // for changes in Organization assignments
    const orgCalls = this.getChangedOrganizationCalls();
    apiCalls.push(...orgCalls);

    // for changes in PrerequisiteAppVersion assignments
    const prereqAppVCalls = this.getChangedPrereqAppCalls();
    apiCalls.push(...prereqAppVCalls);

    return apiCalls;
  }

  private getChangedApplicationPayload(): UpdateApplicationPayload {
    const currentState = this.editForm.getRawValue();
    const payload: UpdateApplicationPayload = {};
    if (currentState.applicationName && currentState.applicationName !== this.application.name) {
      payload.name = currentState.applicationName;
    }
    if (currentState.description && currentState.description !== this.application.description) {
      payload.description = currentState.description;
    }
    if (currentState.isPlugin !== null && currentState.isPlugin !== this.application.isPlugin) {
      payload.isPlugin = currentState.isPlugin;
    }
    return payload;
  }

  private getChangedVersionPayload(): UpdateVersionPayload {
    const currentState = this.editForm.getRawValue();
    const payload: UpdateVersionPayload = {};
    if (currentState.version !== null && currentState.version !== this.appVersion.version) {
      payload.version = currentState.version;
    }
    if (currentState.containerName !== null && currentState.containerName !== this.appVersion.containerName) {
      payload.containerName = currentState.containerName;
    }
    if (currentState.inputs !== null && currentState.inputs !== this.appVersion.inputs) {
      payload.inputs = currentState.inputs;
    }
    if (currentState.multiRun !== null && currentState.multiRun !== this.appVersion.multiRun) {
      payload.multiRun = currentState.multiRun;
    }
    return payload;
  }

  private getChangedOrganizationCalls(): Observable<any>[] {
    const calls: Observable<any>[] = [];
    const versionId = Number(this.appVersion.appVersionId);
    const currentOrgIds = this.editForm.getRawValue().selectedOrgs?.map((org) => org.orgId) ?? [];
    const initialOrgIds = this.appVersion.organizations.map((org) => org.orgId);

    const orgsToAdd = currentOrgIds.filter((orgId) => !initialOrgIds.includes(orgId));
    orgsToAdd.forEach((orgId) => {
      calls.push(this.organizationService.assignOrgToVersion(Number(orgId), versionId));
    });

    const orgsToRemove = initialOrgIds.filter((orgId) => !currentOrgIds.includes(orgId));
    orgsToRemove.forEach((orgId) => {
      calls.push(this.organizationService.unassignOrgToVersion(Number(orgId), versionId));
    });

    return calls;
  }

  private getChangedPrereqAppCalls(): Observable<any>[] {
    const calls: Observable<any>[] = [];
    const appVersionId = this.appVersion.appVersionId;
    const currentPrereqAppVersionIds =
      this.editForm.getRawValue().dependsOnAppVersions?.map((appV) => appV.appVersionId) ?? [];
    const initialPrereqAppVersionIds = this.appVersion.prerequisiteApps.map((appV) => appV.appVersionId);

    const appVersionIdsToAdd = currentPrereqAppVersionIds.filter(
      (appVersionId) => !initialPrereqAppVersionIds.includes(appVersionId)
    );

    if (appVersionIdsToAdd.length > 0) {
      calls.push(this.appService.addPrereqAppVersion(appVersionId, { appVersionIdsToAdd }));
    }

    // const appVersionIdsToRemove = initialPrereqAppVersionIds.filter(
    //   (id) => !currentPrereqAppVersionIds.includes(id)
    // );
    // calls.push(this.appService.removePrereqAppVersion(appVersionId, { appVersionIdsToRemove }));

    return calls;
  }

  // Helper function for error handling
  private handleApiError(error: any, fallbackMessage: string): void {
    this.errorMessage = resolveErrorMessage(error, fallbackMessage);
    this.isErrorVisible = true;
  }

  get applicationName() {
    return this.editForm.get('applicationName');
  }
  get version() {
    return this.editForm.get('version');
  }
  get containerName() {
    return this.editForm.get('containerName');
  }
  get inputs() {
    return this.editForm.get('inputs');
  }
  get selectedOrgs() {
    return this.editForm.get('selectedOrgs');
  }
  get description() {
    return this.editForm.get('description');
  }
  get multiRun() {
    return this.editForm.controls.multiRun;
  }
  get isPlugin() {
    return this.editForm.controls.isPlugin;
  }

  // CheckboxComponent is not a ControlValueAccessor, so bind it to the reactive control manually.
  onMultiRunChange(checked: boolean): void {
    this.multiRun.setValue(checked);
    this.multiRun.markAsDirty();
  }

  onIsPluginChange(checked: boolean): void {
    this.isPlugin.setValue(checked);
    this.isPlugin.markAsDirty();
  }

  isFormChanged(): boolean {
    return this.editForm.dirty;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
